import React, { useState, useEffect, useRef } from 'react';
import { SITE_CONFIG } from '../config/site';
import { useTheme } from '../context/ThemeContext';
import { generateResumePdf } from '../utils/pdfGenerator';
import { exportStandaloneHtml } from '../utils/htmlExporter';
import type { PdfOrientation, ResolvedTheme } from '../types';

interface ResumeModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const FOCUSABLE_SELECTOR = [
  'button:not([disabled]):not([aria-hidden="true"])',
  '[href]:not([disabled]):not([aria-hidden="true"])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"]):not([disabled])',
].join(', ');

export default function ResumeModal({ isOpen, onClose }: ResumeModalProps) {
  const { resolvedTheme } = useTheme();
  const [copyStatus, setCopyStatus] = useState<'idle' | 'copied' | 'error'>('idle');
  const [selectedOrientation, setSelectedOrientation] = useState<PdfOrientation>('portrait');
  const [selectedTheme, setSelectedTheme] = useState<ResolvedTheme>('light');
  const [isGeneratingPdf, setIsGeneratingPdf] = useState<boolean>(false);
  const [isGeneratingHtml, setIsGeneratingHtml] = useState<boolean>(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const [statusMessage, setStatusMessage] = useState<string>('');

  const isGenerating = isGeneratingPdf || isGeneratingHtml;


  const linkedinUrl = import.meta.env.VITE_LINKEDIN_URL || SITE_CONFIG.defaultLinkedinUrl;
  const linkedinVanity = import.meta.env.VITE_LINKEDIN_VANITY || SITE_CONFIG.defaultLinkedinVanity;
  const githubUrl = SITE_CONFIG.githubUrl;

  const modalRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);
  const copyTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (copyTimeoutRef.current) clearTimeout(copyTimeoutRef.current);
    };
  }, []);

  /* Lock background body scroll while modal is active */
  useEffect(() => {
    if (isOpen) {
      const originalOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = originalOverflow;
      };
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      previousFocusRef.current = document.activeElement as HTMLElement | null;
      const focusableElements = modalRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
      if (focusableElements && focusableElements.length > 0) {
        focusableElements[0].focus();
      }
    } else if (previousFocusRef.current && typeof previousFocusRef.current.focus === 'function') {
      previousFocusRef.current.focus();
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
        return;
      }
      if (e.key === 'Tab' && modalRef.current) {
        const focusableElements = modalRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
        if (!focusableElements.length) return;

        const first = focusableElements[0];
        const last = focusableElements[focusableElements.length - 1];

        if (e.shiftKey) {
          if (document.activeElement === first) {
            e.preventDefault();
            last.focus();
          }
        } else {
          if (document.activeElement === last) {
            e.preventDefault();
            first.focus();
          }
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleDownloadPdf = async () => {
    if (isGenerating) return;
    setIsGeneratingPdf(true);
    setExportError(null);
    const orientLabel = selectedOrientation === 'portrait' ? 'Portrait' : 'Landscape';
    const themeLabel = selectedTheme === 'light' ? 'Light Theme' : 'Dark Theme';
    const label = `${orientLabel} (${themeLabel})`;
    setStatusMessage(`Generating ${label} PDF, please wait...`);

    try {
      await generateResumePdf({ orientation: selectedOrientation, theme: selectedTheme });
      setStatusMessage(`${label} PDF downloaded successfully.`);
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Export failed';
      setExportError(`Unable to generate ${label} PDF. ${errorMsg}`);
      setStatusMessage(`Error generating ${label} PDF.`);
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  const handleDownloadHtml = async () => {
    if (isGenerating) return;
    setIsGeneratingHtml(true);
    setExportError(null);
    setStatusMessage('Bundling standalone offline HTML portfolio, please wait...');

    try {
      await exportStandaloneHtml({ activeTheme: resolvedTheme });
      setStatusMessage('Standalone HTML portfolio downloaded successfully.');
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Export failed';
      setExportError(`Unable to export standalone HTML. ${errorMsg}`);
      setStatusMessage('Error bundling standalone HTML.');
    } finally {
      setIsGeneratingHtml(false);
    }
  };

  const handleCopyHandle = async () => {
    if (copyTimeoutRef.current) {
      clearTimeout(copyTimeoutRef.current);
    }
    let success = false;
    if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
      try {
        await navigator.clipboard.writeText(linkedinUrl);
        success = true;
      } catch {
        success = false;
      }
    }
    // Fallback for non-secure contexts (e.g. LAN http access on mobile) or restricted permissions
    if (!success && typeof document !== 'undefined') {
      try {
        const textArea = document.createElement('textarea');
        textArea.value = linkedinUrl;
        textArea.style.position = 'fixed';
        textArea.style.opacity = '0';
        textArea.style.top = '-9999px';
        textArea.style.left = '-9999px';
        textArea.setAttribute('readonly', '');
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        textArea.setSelectionRange(0, 99999);
        success = document.execCommand('copy');
        document.body.removeChild(textArea);
      } catch {
        success = false;
      }
    }

    if (success) {
      setCopyStatus('copied');
      copyTimeoutRef.current = setTimeout(() => setCopyStatus('idle'), 2500);
    } else {
      setCopyStatus('error');
      copyTimeoutRef.current = setTimeout(() => setCopyStatus('idle'), 3000);
    }
  };

  return (
    <div
      className="modal-overlay"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-title"
      aria-busy={isGenerating}
      data-testid="modal-overlay"
    >
      <div
        className="modal-content"
        ref={modalRef}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <div className="modal-header-text">
            <div className="modal-eyebrow-row">
              <span className="modal-eyebrow">Verified Credentials Dispatch</span>
              <span className="modal-security-pill">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                </svg>
                <span>Verified Professional Profile &amp; Governance Compliant</span>
              </span>
            </div>
            <h3 id="modal-title" className="modal-title">Request Full Engineering Résumé</h3>
          </div>
          <button
            type="button"
            className="modal-close-btn"
            onClick={onClose}
            aria-label="Close modal"
          >
            &times;
          </button>
        </div>

        <div className="modal-body">
          <p className="modal-intro">
            Client-side compiled executive résumé with privacy-safe redactions. Select a target format or connect via verified channels:
          </p>

          {/* Dedicated Space-Saving PDF Export Section */}
          <section className="modal-export-section" aria-labelledby="export-section-title">
            <div className="modal-export-header">
              <h4 id="export-section-title" className="modal-section-title">Instant PDF Export</h4>
              <span className="modal-export-badge">Client-Side Vector Engine</span>
            </div>

            <div className="modal-export-toolbar">
              <div className="modal-select-grid">
                <div className="modal-select-wrapper">
                  <label htmlFor="pdf-orientation-select" className="modal-select-label">
                    Layout Orientation
                  </label>
                  <select
                    id="pdf-orientation-select"
                    className="modal-select"
                    value={selectedOrientation}
                    onChange={(e) => setSelectedOrientation(e.target.value as PdfOrientation)}
                    disabled={isGenerating}
                    aria-label="Select PDF Orientation"
                  >
                    <option value="portrait">Portrait (A4 Executive CV)</option>
                    <option value="landscape">Landscape (A4 Presentation)</option>
                  </select>
                </div>

                <div className="modal-select-wrapper">
                  <label htmlFor="pdf-theme-select" className="modal-select-label">
                    Color Theme
                  </label>
                  <select
                    id="pdf-theme-select"
                    className="modal-select"
                    value={selectedTheme}
                    onChange={(e) => setSelectedTheme(e.target.value as ResolvedTheme)}
                    disabled={isGenerating}
                    aria-label="Select PDF Theme"
                  >
                    <option value="light">Light Theme (Print &amp; ATS)</option>
                    <option value="dark">Dark Theme (Cyber Screen)</option>
                  </select>
                </div>
              </div>

              <div className="modal-export-buttons">
                {/* Unified PDF Download Action Button */}
                <button
                  type="button"
                  className={`modal-action-btn primary export-trigger-btn ${isGeneratingPdf ? 'generating' : ''}`}
                  onClick={handleDownloadPdf}
                  disabled={isGenerating}
                  aria-busy={isGeneratingPdf}
                  aria-label="Download Résumé PDF"
                >
                  {isGeneratingPdf ? (
                    <span className="spinner-icon" aria-hidden="true">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <path d="M12 2a10 10 0 0 1 10 10" strokeLinecap="round" />
                      </svg>
                    </span>
                  ) : (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                      <polyline points="7 10 12 15 17 10" />
                      <line x1="12" y1="15" x2="12" y2="3" />
                    </svg>
                  )}
                  <div className="btn-text-block">
                    <strong>{isGeneratingPdf ? 'Compiling Vector PDF...' : 'Download Résumé PDF'}</strong>
                    <span>
                      {selectedOrientation === 'portrait' ? 'A4 Executive CV' : 'A4 Presentation View'} • {selectedTheme === 'light' ? 'Light Theme' : 'Dark Theme'}
                    </span>
                  </div>
                  <span className="modal-btn-badge">
                    {selectedOrientation === 'portrait' ? 'A4 • PORTRAIT' : 'A4 • LANDSCAPE'}
                  </span>
                </button>

                {/* Standalone Single-File Offline HTML Download Button */}
                <button
                  type="button"
                  className={`modal-action-btn html-export-btn export-trigger-btn ${isGeneratingHtml ? 'generating' : ''}`}
                  onClick={handleDownloadHtml}
                  disabled={isGenerating}
                  aria-busy={isGeneratingHtml}
                  aria-label="Download Standalone Offline HTML Portfolio"
                >
                  {isGeneratingHtml ? (
                    <span className="spinner-icon" aria-hidden="true">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <path d="M12 2a10 10 0 0 1 10 10" strokeLinecap="round" />
                      </svg>
                    </span>
                  ) : (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <polyline points="16 18 22 12 16 6" />
                      <polyline points="8 6 2 12 8 18" />
                    </svg>
                  )}
                  <div className="btn-text-block">
                    <strong>{isGeneratingHtml ? 'Bundling Offline HTML...' : 'Download Standalone HTML'}</strong>
                    <span>Full offline interactive portfolio • Single .html file</span>
                  </div>
                  <span className="modal-btn-badge">
                    OFFLINE • HTML
                  </span>
                </button>
              </div>
            </div>
          </section>

          {/* Accessible Live Region */}
          <div className="sr-only" role="status" aria-live="polite" aria-atomic="true">
            {statusMessage}
          </div>

          {exportError && (
            <div className="modal-export-error" role="alert">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
              <span>{exportError}</span>
            </div>
          )}

          <div className="modal-section-divider">
            <span>Direct Profile &amp; Verification</span>
          </div>

          <div className="modal-channels-container">
            <div className="modal-channels-grid">
              <a
                href={linkedinUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="modal-channel-card linkedin-card"
                aria-label="Connect on LinkedIn"
              >
                <div className="channel-icon-wrap">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                    <path d="M19 3a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h14m-.5 15.5v-5.3a3.26 3.26 0 0 0-3.26-3.26c-.85 0-1.84.52-2.28 1.3v-1.11h-2.79v8.37h2.79v-4.93c0-.77.62-1.4 1.39-1.4a1.4 1.4 0 0 1 1.4 1.4v4.93h2.75M6.88 8.56a1.68 1.68 0 0 0 1.68-1.68c0-.93-.75-1.69-1.68-1.69a1.69 1.69 0 0 0-1.69 1.69c0 .93.76 1.68 1.69 1.68m1.39 9.94v-8.37H5.5v8.37h2.77z"/>
                  </svg>
                </div>
                <div className="channel-text-block">
                  <strong>Connect on LinkedIn</strong>
                  <span>in/{linkedinVanity}</span>
                </div>
                <span className="channel-external-arrow" aria-hidden="true">↗</span>
              </a>

              <a
                href={githubUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="modal-channel-card github-card"
                aria-label="Explore Source Code &amp; Repositories"
              >
                <div className="channel-icon-wrap">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                    <path fillRule="evenodd" clipRule="evenodd" d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.53 1.032 1.53 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z"/>
                  </svg>
                </div>
                <div className="channel-text-block">
                  <strong>Explore Repositories</strong>
                  <span>github.com/nagendradixit647</span>
                </div>
                <span className="channel-external-arrow" aria-hidden="true">↗</span>
              </a>
            </div>

            <button
              type="button"
              className={`modal-copy-bar ${copyStatus === 'copied' ? 'copied' : copyStatus === 'error' ? 'error' : ''}`}
              onClick={handleCopyHandle}
              aria-label="Copy LinkedIn Profile URL"
              aria-live="polite"
            >
              <div className="copy-bar-left">
                {copyStatus === 'copied' ? (
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                ) : (
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                  </svg>
                )}
                <div className="copy-bar-text">
                  <strong role="status">
                    {copyStatus === 'copied'
                      ? 'Copied to Clipboard!'
                      : copyStatus === 'error'
                        ? 'Failed to copy to clipboard'
                        : 'Copy LinkedIn Profile URL'}
                  </strong>
                  <span className="copy-bar-url">
                    {copyStatus === 'copied'
                      ? 'Ready to paste into your browser or messenger'
                      : copyStatus === 'error'
                        ? 'Please copy URL manually from below'
                        : linkedinUrl}
                  </span>
                </div>
              </div>
              <span className="copy-badge">
                {copyStatus === 'copied' ? 'COPIED' : 'CLICK TO COPY'}
              </span>
            </button>
          </div>
        </div>

        <div className="modal-footer">
          <span className="modal-footer-hint">Vector PDF &amp; Standalone HTML • Privacy Safe</span>
          <button type="button" className="btn-modal-dismiss" onClick={onClose}>
            Close Window
          </button>
        </div>
      </div>
    </div>
  );
}
